<template>
  <app-layout title="Customer Setup">
    <page-header>
      <template #title>Setup – {{ customer.name }}</template>
      <template #actions>
        <jet-tertiary-button :href="backUrl">Close</jet-tertiary-button>
      </template>
    </page-header>

    <customer-navigation :customer="customer" />

    <div class="px-8 py-4 space-y-6">

      <!-- Reopen Application header (only visible for rejected customers) -->
      <div
        v-if="showRejectedHeader"
        class="p-4 border rounded bg-amber-50 border-amber-200"
      >
        <div>
          <div class="font-semibold text-amber-900">
            This application has been rejected.
          </div>
          <div class="mt-1 text-sm text-amber-800">
            You can reopen the application to allow the customer to submit again.
          </div>
          <div class="mt-4 flex flex-wrap items-center gap-2">
            <jet-button
              type="button"
              color="red"
              :disabled="reopenInProgress"
              :class="{ 'opacity-25 cursor-not-allowed': reopenInProgress }"
              @click="openReopenModal"
            >
              Reopen Application
            </jet-button>
          </div>
        </div>
      </div>

      <!-- Activation Header (only visible by sales rep) -->
      <div
        v-if="showActivationHeader"
        :class="[
          'p-4 border rounded',
          activationMissingFieldsList.length > 0
            ? 'bg-amber-50 border-amber-200'
            : 'bg-green-50 border-green-200'
        ]"
      >
        <div>
          <div
            v-if="activationMissingFieldsList.length > 0"
            class="font-semibold text-amber-900"
          >
            Complete the following in the customer profile to activate:
          </div>
          <div
            v-else
            class="font-semibold text-green-900"
          >
            All required fields are complete.
          </div>
          <ul
            v-if="activationMissingFieldsList.length > 0"
            class="mt-2 text-sm text-amber-800 list-disc list-inside"
          >
            <li v-for="label in activationMissingFieldsList" :key="label">{{ label }}</li>
          </ul>
          <div v-else class="mt-1 text-sm text-green-800">
            You can activate this customer.
          </div>

          <div class="mt-4 flex flex-wrap items-center gap-2">
            <jet-button
              :disabled="activateInProgress || activationMissingFieldsList.length > 0"
              :class="{ 'opacity-25 cursor-not-allowed': activateInProgress || activationMissingFieldsList.length > 0 }"
              @click="activate"
            >
              Activate
            </jet-button>
            <jet-button :href="route('customers.edit', props.customer.id)">
              Go to customer profile
            </jet-button>
          </div>
        </div>
      </div>

      <!-- Business Details -->
      <div class="p-4 border rounded">
        <div class="flex items-start justify-between gap-4">
          <div>
            <div class="font-semibold">Business Details</div>
            <div class="mt-1 text-sm text-gray-600">
              Status:
              <span class="font-medium text-gray-800">
                {{
                  onboarding?.status === 'business_details_submitted'
                    ? 'Pending approval'
                    : formatEnum(onboarding?.status)
                }}
              </span>
            </div>
            <div class="mt-1 text-sm text-gray-600">
              Latest submission:
              <span class="font-medium text-gray-800">
                {{ latestBusinessDetailsSubmission?.created_at ? formatDate(latestBusinessDetailsSubmission.created_at) : '—' }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="!latestBusinessDetailsSubmission" class="mt-4 text-sm text-gray-500">
          No submission yet.
        </div>

        <div v-else class="mt-4 space-y-6">
          <!-- General -->
          <section>
            <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">General</div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <kv label="Business Name" :value="bd.business_name" />
              <kv label="Phone" :value="bd.phone" />
              <kv label="Business Contact" :value="bd.business_contact" />
              <kv label="Business Email" :value="bd.business_email" />
              <kv label="Years in Business" :value="bd.years_in_business" />
              <kv label="AP Contact" :value="bd.contact_name" />
              <kv label="AP Email" :value="bd.ap_email" />
            </div>
          </section>

          <!-- Address -->
          <section>
            <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Address</div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <kv label="Address" :value="bd.address_1" />
              <kv label="City" :value="bd.city" />
              <kv label="Province" :value="bd.province" />
              <kv label="Postal Code" :value="bd.postal_code" />
            </div>
          </section>

          <!-- Payment Preferences -->
          <section>
            <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Payment Preferences</div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <kv label="Payment Method" :value="formatEnum(bd.payment_method)" />
              <kv label="Payment Terms" :value="formatEnum(bd.payment_terms)" />
              <kv v-if="bd.payment_method === 'exception'" label="Exception Reason" :value="bd.exception_notes" />
            </div>
          </section>

          <!-- Trade References -->
          <section>
            <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Trade References</div>

            <div v-if="bd.no_trade_references" class="text-sm text-gray-600">
              Customer indicated: <span class="font-medium">No trade references</span>.
            </div>

            <div v-else-if="!hasAnyTradeRefs" class="text-sm text-gray-500">
              No trade references provided.
            </div>

            <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div class="border rounded p-3">
                <div class="font-semibold text-sm mb-2">Trade Reference #1</div>
                <div class="grid grid-cols-1 gap-2">
                  <kv label="Company Name" :value="tr1.company_name" />
                  <kv label="Company Address" :value="tr1.company_address" />
                  <kv label="Contact Name" :value="tr1.contact_name" />
                  <kv label="Phone" :value="tr1.phone" />
                  <kv label="Email" :value="tr1.email" />
                  <kv label="Account Opened Since" :value="tr1.account_opened_since" />
                  <kv label="Current Balance" :value="tr1.current_balance" />
                </div>
              </div>

              <div class="border rounded p-3">
                <div class="font-semibold text-sm mb-2">Trade Reference #2</div>
                <div class="grid grid-cols-1 gap-2">
                  <kv label="Company Name" :value="tr2.company_name" />
                  <kv label="Company Address" :value="tr2.company_address" />
                  <kv label="Contact Name" :value="tr2.contact_name" />
                  <kv label="Phone" :value="tr2.phone" />
                  <kv label="Email" :value="tr2.email" />
                  <kv label="Account Opened Since" :value="tr2.account_opened_since" />
                  <kv label="Current Balance" :value="tr2.current_balance" />
                </div>
              </div>
            </div>
          </section>

          <!-- Signature -->
          <section>
            <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Authorization</div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <kv label="Signed By" :value="bd.signature_name" />
            </div>

            <div v-if="signatureUrl" class="mt-3 p-3">
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Signature</div>
              <img
                :src="signatureUrl"
                alt="Signature"
                class="max-w-full max-h-48 object-contain bg-white border rounded p-2"
              />
            </div>
            <div v-else class="mt-2 text-sm text-gray-500">
              No signature file found.
            </div>
          </section>

          <!-- Approve / Request Changes / Reject Customer (hidden for activated customers) -->
          <div v-if="!isCustomerActivated" class="flex items-center gap-2">
            <jet-button
              v-if="canReviewBusinessDetails"
              :disabled="bdActionForm.processing || onboarding.status !== 'business_details_submitted'"
              class="leading-none"
              @click="approveBusinessDetails"
            >
              Approve
            </jet-button>

            <jet-button
              v-if="canReviewBusinessDetails"
              color="yellow"
              :disabled="bdActionForm.processing || onboarding.status !== 'business_details_submitted'"
              class="leading-none"
              @click="openRejectBDModal"
            >
              Request Changes
            </jet-button>

            <jet-danger-button
              v-if="canReviewBusinessDetails && canRejectCustomer"
              :disabled="rejectCustomerForm.processing"
              class="leading-none"
              @click="openRejectCustomerModal"
            >
              Reject Customer
            </jet-danger-button>
          </div>
        </div>
      </div>

      <!-- Payment Details -->
      <div class="p-4 border rounded">
        <div class="flex items-start justify-between gap-4">
          <div>
            <div class="font-semibold">Payment Details</div>

            <div class="mt-1 text-sm text-gray-600">
              <span class="font-medium text-gray-800">
                Status:
                  {{
                    onboarding?.status === 'payment_details_submitted'
                      ? 'Pending approval'
                      : formatEnum(onboarding?.status)
                  }}
              </span>
            </div>

            <div class="mt-1 text-sm text-gray-600">
              Latest submission:
              <span class="font-medium text-gray-800">
                {{ latestPaymentDetailsSubmission?.created_at ? formatDate(latestPaymentDetailsSubmission.created_at) : '—' }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="!latestPaymentDetailsSubmission" class="mt-4 text-sm text-gray-500">
          No submission yet.
        </div>

        <div v-else class="mt-4 space-y-6">
          <!-- CREDIT CARD -->
          <div v-if="isPaymentCreditCard" class="space-y-6">
            <section>
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Billing Address</div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <kv label="Name" :value="pd.first_name + ' ' + pd.last_name" />
                <kv label="Phone" :value="pd.phone" />
                <kv label="Address" :value="pd.street_number + ' ' + pd.street_name" />
                <kv label="City" :value="pd.city" />
                <kv label="Province" :value="pd.province" />
                <kv label="Postal Code" :value="pd.postal_code" />
              </div>
            </section>

            <section>
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Card Information</div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <kv label="Brand" :value="pd.brand ?? customer?.vault?.brand" />
                <kv label="Last Four Digits" :value="pd.last_four ?? customer?.vault?.last_four" />
                <kv label="Expiry" :value="pd.expiry ?? customer?.vault?.expiry" />
              </div>
            </section>
          </div>

          <!-- NON CREDIT CARD -->
          <div v-else class="space-y-6">
            <section>
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Banking Details</div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <kv label="Account Holder Name" :value="pd.account_holder_name" />
                <kv label="Bank Name" :value="pd.bank_name" />
                <kv label="Account Type" :value="pd.account_type" />
              </div>
            </section>

            <section>
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Routing</div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <kv label="Account Number" :value="pd.account_number" />
                <kv label="Transit Number" :value="pd.transit_number" />
                <kv label="Institution Number" :value="pd.institution_number" />
              </div>
            </section>

            <!-- DEFT banking documents (download + filename; access enforced by backend) -->
            <section v-if="pd.payment_method === 'deft' && paymentDetailsDeftUploads.length">
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Banking documents (DEFT)</div>
              <ul class="space-y-2">
                <li
                  v-for="upload in paymentDetailsDeftUploads"
                  :key="upload.id"
                  class="flex items-center gap-3 text-sm"
                >
                  <jet-tertiary-button
                    type="button"
                    class="shrink-0 leading-none"
                    @click="downloadDeftFile(upload.download_url)"
                  >
                    Download
                  </jet-tertiary-button>
                  <span class="font-medium text-gray-800 break-all">{{ upload.original_name }}</span>
                  <span v-if="upload.delete_at" class="text-xs text-gray-500 shrink-0">
                    (This file will be automatically deleted on {{ formatDeftDeleteDate(upload.delete_at) }})
                  </span>
                </li>
              </ul>
            </section>
          </div>

          <!-- Approve / Request Changes / Reject Customer (hidden for activated customers) -->
          <div v-if="!isCustomerActivated" class="flex items-center gap-2">
            <jet-button
              v-if="canReviewPaymentDetails"
              :disabled="paymentActionForm.processing || onboarding.status !== 'payment_details_submitted'"
              class="leading-none"
              @click="approvePaymentDetails"
            >
              Approve
            </jet-button>

            <jet-button
              v-if="canReviewPaymentDetails"
              color="yellow"
              :disabled="paymentActionForm.processing || onboarding.status !== 'payment_details_submitted'"
              class="leading-none"
              @click="openRejectPaymentModal"
            >
              Request Changes
            </jet-button>

            <jet-danger-button
              v-if="canReviewPaymentDetails && canRejectCustomer"
              :disabled="rejectCustomerForm.processing"
              class="leading-none"
              @click="openRejectCustomerModal"
            >
              Reject Customer
            </jet-danger-button>
          </div>
        </div>
      </div>

      <div v-if="!isCustomerActivated" class="w-full text-center my-8">
        <jet-danger-button :disabled="deleteInProgress" @click="showDeleteModal = true">
          Delete Customer
        </jet-danger-button>
      </div>

      <!-- Request Changes: Business Details Modal -->
      <jet-confirmation-modal :show="showRejectBDModal" @close="closeRejectBDModal">
        <template #title>
          Request Changes – Business Details
        </template>

        <template #content>
          <div class="text-sm text-gray-700">
            Please provide a reason for requesting changes to this customer's business details. The customer will be able to re-submit to address any feedback.
          </div>

          <div class="mt-3">
            <textarea
              v-model="bdActionForm.rejected_reason"
              rows="4"
              class="w-full border rounded p-2 text-sm"
              placeholder="e.g. Invalid address, incorrect email, etc."
            />
            <div v-if="$page.props.errors?.rejected_reason" class="text-sm text-red-600 mt-1">
              {{ $page.props.errors.rejected_reason }}
            </div>
          </div>
        </template>

        <template #footer>
          <jet-button
            color="yellow"
            :disabled="bdActionForm.processing || !canSubmitBDReject"
            :class="{ 'opacity-25': bdActionForm.processing || !canSubmitBDReject }"
            @click="rejectBusinessDetails"
          >
            Request Changes
          </jet-button>

          <jet-tertiary-button class="ml-2" :disabled="bdActionForm.processing" @click="closeRejectBDModal">
            Cancel
          </jet-tertiary-button>
        </template>
      </jet-confirmation-modal>
      <!-- Reject Payment Details Modal -->
      <jet-confirmation-modal :show="showRejectPaymentModal" @close="closeRejectPaymentModal">
        <template #title>
          Request Changes – Payment Details
        </template>

        <template #content>
          <div class="text-sm text-gray-700">
            Please provide a reason for requesting changes to this customer's payment details. The customer will be able to re-submit to address your feedback.
          </div>

          <div class="mt-3">
            <textarea
              v-model="paymentActionForm.rejected_reason"
              rows="4"
              class="w-full border rounded p-2 text-sm"
              placeholder="e.g. Card verification failed, bank details incomplete, etc."
            />
            <div v-if="$page.props.errors?.rejected_reason" class="text-sm text-red-600 mt-1">
              {{ $page.props.errors.rejected_reason }}
            </div>
          </div>
        </template>

        <template #footer>
          <jet-button
            color="yellow"
            :disabled="paymentActionForm.processing || !canSubmitPaymentReject"
            :class="{ 'opacity-25': paymentActionForm.processing || !canSubmitPaymentReject }"
            @click="rejectPaymentDetails"
          >
            Request Changes
          </jet-button>

          <jet-tertiary-button class="ml-2" :disabled="paymentActionForm.processing" @click="closeRejectPaymentModal">
            Cancel
          </jet-tertiary-button>
        </template>
      </jet-confirmation-modal>

      <!-- Reject Customer (prevents all further submissions) -->
      <jet-confirmation-modal :show="showRejectCustomerModal" @close="closeRejectCustomerModal">
        <template #title>
          Reject Customer
        </template>

        <template #content>
          <div class="text-sm text-gray-700">
            Rejecting this customer will set their application status to rejected and <strong>prevent all further submissions</strong>. They will not be able to submit or re-submit business or payment details.
          </div>

          <div class="mt-3">
            <label for="reject_customer_reason" class="block text-sm font-medium text-gray-700 mb-1">Reason for rejection:</label>
            <textarea
              id="reject_customer_reason"
              v-model="rejectCustomerForm.rejected_reason"
              rows="4"
              class="w-full border rounded p-2 text-sm"
              placeholder="e.g. Application no longer needed, duplicate, etc."
            />
            <div v-if="$page.props.errors?.rejected_reason" class="text-sm text-red-600 mt-1">
              {{ $page.props.errors.rejected_reason }}
            </div>
          </div>
        </template>

        <template #footer>
          <jet-danger-button
            :disabled="rejectCustomerForm.processing"
            :class="{ 'opacity-25': rejectCustomerForm.processing }"
            @click="rejectCustomer"
          >
            Reject Customer
          </jet-danger-button>

          <jet-tertiary-button class="ml-2" :disabled="rejectCustomerForm.processing" @click="closeRejectCustomerModal">
            Cancel
          </jet-tertiary-button>
        </template>
      </jet-confirmation-modal>

      <!-- Reopen Application confirmation -->
      <jet-confirmation-modal :show="showReopenModal" @close="closeReopenModal">
        <template #title>
          Reopen Application
        </template>

        <template #content>
          <div class="text-sm text-gray-700">
            Reopening will restart the application process for this customer. They will be able to submit business and payment details again.
          </div>
        </template>

        <template #footer>
          <jet-button
            color="red"
            :disabled="reopenInProgress"
            :class="{ 'opacity-25 cursor-not-allowed': reopenInProgress }"
            @click="confirmReopenApplication"
          >
            Reopen Application
          </jet-button>

          <jet-tertiary-button class="ml-2" :disabled="reopenInProgress" @click="closeReopenModal">
            Cancel
          </jet-tertiary-button>
        </template>
      </jet-confirmation-modal>

      <!-- Delete Customer -->
      <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal = false">
        <template #title>
          Delete Customer
        </template>

        <template #content>
          Are you sure you would like to delete this customer?
        </template>

        <template #footer>
          <jet-tertiary-button @click="showDeleteModal = false">
            Cancel
          </jet-tertiary-button>

          <jet-danger-button
            class="ml-2"
            :class="{ 'opacity-25': deleteInProgress }"
            :disabled="deleteInProgress"
            @click="deleteCustomer"
          >
            Delete
          </jet-danger-button>
        </template>
      </jet-confirmation-modal>
    </div>
  </app-layout>
</template>

<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHeader from '@/Layouts/partials/PageHeader.vue'
import CustomerNavigation from '@/Shared/CustomerNavigation'
import JetButton from '@/Jetstream/Button'
import JetDangerButton from '@/Jetstream/DangerButton'
import JetTertiaryButton from '@/Jetstream/TertiaryButton'
import JetConfirmationModal from '@/Jetstream/ConfirmationModal'
import { computed, ref } from 'vue'
import { useForm } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';

const props = defineProps({
  customer: Object,
  onboarding: Object,
  latestBusinessDetailsSubmission: Object,
  latestPaymentDetailsSubmission: Object,
  paymentDetailsDeftUploads: { type: Array, default: () => [] },
  approveBusinessDetailsRoute: { type: String, required: true },
  rejectBusinessDetailsRoute: { type: String, required: true },
  approvePaymentDetailsRoute: { type: String, required: true },
  rejectPaymentDetailsRoute: { type: String, required: true },
  rejectCustomerRoute: { type: String, required: true },
  reopenApplicationRoute: { type: String, default: null },
  canActivate: { type: Boolean, default: false },
  activateCustomerRoute: { type: String, default: null },
  activationMissingFields: { type: Array, default: () => [] },
})

// Computed payloads, forms and helpers
const bd = computed(() => props.latestBusinessDetailsSubmission?.payload ?? {})
const pd = computed(() => props.latestPaymentDetailsSubmission?.payload ?? {})
const showRejectBDModal = ref(false)
const showRejectPaymentModal = ref(false)
const showRejectCustomerModal = ref(false)
const showReopenModal = ref(false)
const showDeleteModal = ref(false)
const deleteInProgress = ref(false)

const isPaymentCreditCard = computed(() => {
  return ['credit_card', 'credit_card_manual'].includes(pd.value.payment_method)
})

const bdActionForm = useForm({
  rejected_reason: '',
})

const paymentActionForm = useForm({
  rejected_reason: '',
})

const rejectCustomerForm = useForm({
  rejected_reason: '',
})

const activateInProgress = ref(false)
const reopenInProgress = ref(false)

const showRejectedHeader = computed(() => {
  return props.reopenApplicationRoute != null &&
    (props.customer?.onboarding_status === 'rejected' || props.onboarding?.status === 'customer_rejected')
})

// Show activation header and Activate button when payment details approved and user can activate
const showActivationHeader = computed(() => {
  return props.canActivate && props.onboarding?.status === 'payment_details_approved'
})

const activationMissingFieldsList = computed(() => {
  return Array.isArray(props.activationMissingFields) ? props.activationMissingFields : []
})

const isCustomerActivated = computed(() => {
  return props.customer?.onboarding_status === 'activated' || props.onboarding?.status === 'activated'
})

// Validation, approval and rejection functions
const canReviewBusinessDetails = computed(() => {
  return !!props.latestBusinessDetailsSubmission && ['business_details_submitted', 'business_details_correction_required'].includes(props.onboarding?.status)
})

const canReviewPaymentDetails = computed(() => {
  return !!props.latestPaymentDetailsSubmission && ['payment_details_submitted', 'payment_details_correction_required'].includes(props.onboarding?.status)
})

// Reject Customer is available whenever we're in review mode and the customer is not already rejected or activated
const canRejectCustomer = computed(() => {
  const status = props.onboarding?.status
  return status && !['customer_rejected', 'activated'].includes(status)
})

const canSubmitBDReject = computed(() => {
  return String(bdActionForm.rejected_reason || '').trim().length > 0
})

const canSubmitPaymentReject = computed(() => {
  return String(paymentActionForm.rejected_reason || '').trim().length > 0
})

function approveBusinessDetails() {
  if (bdActionForm.processing) return

  bdActionForm.clearErrors()
  bdActionForm.rejected_reason = ''

  bdActionForm.post(props.approveBusinessDetailsRoute, {
    preserveScroll: false,
  })
}

function approvePaymentDetails() {
  if (paymentActionForm.processing) return

  paymentActionForm.clearErrors()
  paymentActionForm.rejected_reason = ''

  paymentActionForm.post(props.approvePaymentDetailsRoute, {
    preserveScroll: false,
  })
}


function rejectBusinessDetails() {
  if (bdActionForm.processing) return

  bdActionForm.post(props.rejectBusinessDetailsRoute, {
    preserveScroll: false,
    onSuccess: () => {
      showRejectBDModal.value = false
      bdActionForm.rejected_reason = ''
    },
  })
}

function rejectPaymentDetails() {
  if (paymentActionForm.processing) return

  paymentActionForm.post(props.rejectPaymentDetailsRoute, {
    preserveScroll: false,
    onSuccess: () => {
      showRejectPaymentModal.value = false
      paymentActionForm.rejected_reason = ''
    },
  })
}

function openReopenModal() {
  showReopenModal.value = true
}

function closeReopenModal() {
  showReopenModal.value = false
}

function confirmReopenApplication() {
  if (!props.reopenApplicationRoute || reopenInProgress.value) return
  reopenInProgress.value = true
  Inertia.post(props.reopenApplicationRoute, {}, {
    preserveScroll: false,
    onSuccess: () => { showReopenModal.value = false },
    onFinish: () => { reopenInProgress.value = false },
  })
}

// Full-page navigation so the browser receives the file and triggers download (avoids Inertia XHR)
function downloadDeftFile(url) {
  window.location.href = url
}

// Modal Helper functions
function openRejectBDModal() {
  bdActionForm.clearErrors()
  bdActionForm.rejected_reason = ''
  showRejectBDModal.value = true
}

function openRejectPaymentModal() {
  paymentActionForm.clearErrors()
  paymentActionForm.rejected_reason = ''
  showRejectPaymentModal.value = true
}

function closeRejectBDModal() {
  showRejectBDModal.value = false
}

function closeRejectPaymentModal() {
  showRejectPaymentModal.value = false
}

function openRejectCustomerModal() {
  rejectCustomerForm.clearErrors()
  rejectCustomerForm.rejected_reason = ''
  showRejectCustomerModal.value = true
}

function closeRejectCustomerModal() {
  showRejectCustomerModal.value = false
}

// Builds a CustomerRequest-shaped payload for customer activation 
function activate() {
  if (activateInProgress.value || activationMissingFieldsList.value.length > 0) return
  const c = props.customer

  const schedules = (c.delivery_schedules || []).map((row) => ({
    delivery_days: typeof row.delivery_days === 'string'
      ? row.delivery_days.split(',').map((d) => d.trim()).filter(Boolean)
      : [...(row.delivery_days || [])],
    delivery_window_start: row.delivery_window_start
      ? String(row.delivery_window_start).slice(0, 5)
      : null,
    delivery_window_end: row.delivery_window_end
      ? String(row.delivery_window_end).slice(0, 5)
      : null,
  }))

  const pricing = (c.pricing || []).map((p) => ({
    product_id: p.product_id,
    price: typeof p.price === 'number' ? p.price.toFixed(2) : String(p.price ?? ''),
    modifier_type: p.modifier_type,
    modifier_amount: p.modifier_amount,
  }))

  const invoiceEmails = Array.isArray(c.invoice_emails)
    ? c.invoice_emails.join(',')
    : (c.invoice_emails ?? '')

  const payload = {
    setup_mode: 'sales',
    enabled: 1,
    name: c.name,
    address_1: c.address_1,
    address_2: c.address_2 ?? null,
    city: c.city,
    postal_code: c.postal_code,
    province: c.province,
    pricing_level_id: c.pricing_level_id,
    type: c.type,
    route: c.route,
    default_shipping_method: c.default_shipping_method,
    delivery_schedules: schedules.length
      ? schedules
      : [{ delivery_days: [], delivery_window_start: null, delivery_window_end: null }],
    delivery_duration: c.delivery_duration ?? null,
    auth_code: null,
    phone: c.phone ?? null,
    cell: c.cell ?? null,
    fax: c.fax ?? null,
    email: c.email ?? null,
    ap_email: c.ap_email ?? null,
    contact_name: c.contact_name ?? null,
    delivery_order_minimum: c.delivery_order_minimum ?? null,
    delivery_fee: c.delivery_fee ?? null,
    auto_apply_credit: c.auto_apply_credit ?? null,
    credit_balance: c.credit_balance ?? null,
    internal_notes: c.internal_notes ?? null,
    order_notes: c.order_notes ?? null,
    load_notes: c.load_notes ?? null,
    payment_terms: c.payment_terms ?? null,
    payment_method: c.payment_method ?? null,
    tax_exempt: c.tax_exempt,
    show_printed_order_pricing: c.show_printed_order_pricing,
    pallet_orders: c.pallet_orders,
    send_inactive_alerts: c.send_inactive_alerts ?? null,
    inactive_alert_weeks: c.inactive_alert_weeks ?? null,
    inactive_alert_last_sent_at: c.inactive_alert_last_sent_at ?? null,
    sales_rep_id: c.sales_rep_id ?? null,
    po_num: c.po_num ?? null,
    invoice_emails: invoiceEmails,
    pricing,
  }

  activateInProgress.value = true
  Inertia.post(props.activateCustomerRoute, payload, {
    preserveScroll: false,
    onFinish: () => {
      activateInProgress.value = false
    },
  })
}

function rejectCustomer() {
  if (rejectCustomerForm.processing) return

  rejectCustomerForm.post(props.rejectCustomerRoute, {
    preserveScroll: false,
    onSuccess: () => {
      showRejectCustomerModal.value = false
      rejectCustomerForm.rejected_reason = ''
    },
  })
}

function deleteCustomer() {
  if (deleteInProgress.value) return

  deleteInProgress.value = true
  showDeleteModal.value = false

  Inertia.delete(route('customers.destroy', props.customer.id), {
    preserveScroll: false,
    onFinish: () => {
      deleteInProgress.value = false
    },
  })
}

// trade references
const tr1 = computed(() => bd.value.trade_reference_1 ?? {})
const tr2 = computed(() => bd.value.trade_reference_2 ?? {})

const hasAnyTradeRefs = computed(() => {
  const hasAnyValue = (obj) =>
    obj && Object.values(obj).some(v => v != null && String(v).trim() !== '')
  return hasAnyValue(tr1.value) || hasAnyValue(tr2.value)
})

const signatureUrl = computed(() => {
  const filename =
    props.latestBusinessDetailsSubmission?.signature_filepath ||
    bd.value.signature_filename

  if (!filename) return null
  return `/get_customer_business_details_signature?filename=${encodeURIComponent(filename)}`
})

function formatDate(value) {
  if (!value) return '—'

  const normalized = value.replace(/\.\d+Z$/, 'Z')

  const d = new Date(normalized)
  if (isNaN(d.getTime())) return value

  return d.toLocaleString()
}

function formatDeftDeleteDate(dateString) {
  if (!dateString) return '—'
  const d = new Date(dateString)
  if (isNaN(d.getTime())) return dateString
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

// takes enums_like_this and formats to Enums Like This
function formatEnum(value) {
  if (value == null) return '—'

  const s = String(value).trim()

  if (s == 'business_details_correction_required' || value == 'payment_details_correction_required') return 'Changes Requested'

  if (!s) return '—'

  return s
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

const kv = {
  props: { label: String, value: [String, Number, Boolean, Object] },
  template: `
    <div class="text-sm">
      <div class="text-xs text-gray-500">{{ label }}</div>
      <div class="text-gray-800 font-medium break-words">
        <span v-if="value === false">No</span>
        <span v-else-if="value === true">Yes</span>
        <span v-else-if="value == null || String(value).trim() === ''" class="text-gray-400">—</span>
        <span v-else>{{ value }}</span>
      </div>
    </div>
  `,
}

const backUrl = route('customers.index', {
  page: window?.page?.props?.page?.customers,
})
</script>
